import React, { useState, useEffect } from 'react';
import { UserManagementService } from '../../shared/services/UserManagementService';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { 
  Users, 
  Plus, 
  Mail, 
  Search,
  Eye,
  Edit3,
  UserCheck,
  UserX,
  Shield,
  AlertCircle,
  CheckCircle,
  XCircle,
  MoreVertical,
  Copy,
  Send,
  Calendar
} from 'lucide-react';

/**
 * TenantUserManagement - User management interface for tenant admins
 * Allows tenant admins to manage users within their own company
 */
export default function TenantUserManagement({ companyId, userProfile, companyData }) {
  const [tenantUsers, setTenantUsers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [tenantStats, setTenantStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);

  useEffect(() => {
    if (companyId) {
      loadTenantUsers();
    }
  }, [companyId]);

  /**
   * Load users for current tenant
   */
  const loadTenantUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 Loading tenant users for:', companyId);

      // CHA-97 / PR-C: listTenantUsers returns ONLY this tenant's users
      // (server-side filter via the listUsers callable). Pre-PR-C this
      // fetched all tenants' users + filtered client-side — wasteful
      // and broke at the rules layer post-deploy.
      const [currentTenantUsers, allInvitations] = await Promise.all([
        UserManagementService.listTenantUsers(companyId),
        UserManagementService.getPendingInvitations()
      ]);

      // Invitations are still fetched globally + filtered client-side
      // (user_invitations collection is out of CHA-97 scope; CHA-112
      // territory).
      const currentTenantInvitations = allInvitations.filter(inv => inv.companyId === companyId);
      
      setTenantUsers(currentTenantUsers);
      setPendingInvitations(currentTenantInvitations);
      
      // Calculate tenant-specific stats
      const stats = calculateTenantStats(currentTenantUsers, currentTenantInvitations);
      setTenantStats(stats);
      
      console.log(`✅ Loaded ${currentTenantUsers.length} users for tenant:`, companyId);
      
    } catch (err) {
      console.error('❌ Failed to load tenant users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calculate tenant-specific statistics
   */
  const calculateTenantStats = (users, invitations) => {
    const activeUsers = users.filter(u => !u.disabled).length;
    const inactiveUsers = users.filter(u => u.disabled === true).length;

    // CHA-97 / PR-C: recentlyActive metric dropped — listUsers does not
    // surface lastSignInTime, and userProfiles.lastLogin is gone.

    const roleBreakdown = users.reduce((acc, user) => {
      if (user.roleId) acc[user.roleId] = (acc[user.roleId] || 0) + 1;
      return acc;
    }, {});

    return {
      total: users.length,
      active: activeUsers,
      inactive: inactiveUsers,
      pendingInvitations: invitations.length,
      roleBreakdown
    };
  };

  /**
   * Handle user invitation to tenant
   */
  const handleInviteUser = async (inviteData) => {
    try {
      console.log('📧 Inviting user to tenant:', companyId, inviteData);
      
      const result = await UserManagementService.inviteUserToTenant({
        ...inviteData,
        companyId: companyId
      });
      
      // Generate invitation link
      const baseUrl = window.location.origin;
      const invitationLink = `${baseUrl}/invite/${result.id}`;
      
      // Show success message with invitation link
      alert(`✅ User invited successfully!\n\nInvitation sent to: ${inviteData.email}\nRole: ${UserManagementService.formatRoleName(inviteData.role)}\nCompany: ${companyData?.name || companyId}\n\nInvitation link: ${invitationLink}\n\n(In production, this would be sent via email)`);
      
      // Refresh data
      await loadTenantUsers();
      setShowInviteModal(false);
      
    } catch (err) {
      console.error('❌ Failed to invite user:', err);
      setError(err.message);
    }
  };

  /**
   * Handle user role update.
   *
   * CHA-97 / PR-C: updateUserRole signature added tenantId. This
   * component is scoped to a single tenant via the `companyId` prop,
   * so we pass that as tenantId.
   */
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      console.log('🔄 Updating user role:', userId, 'to', newRole, 'tenant:', companyId);

      await UserManagementService.updateUserRole(userId, companyId, newRole);

      // Refresh users
      await loadTenantUsers();
      setShowEditUserModal(false);
      setSelectedUser(null);

      console.log('✅ User role updated successfully');
    } catch (err) {
      console.error('❌ Failed to update user role:', err);
      setError(err.message);
    }
  };

  /**
   * Handle user status toggle.
   *
   * CHA-97 / PR-C: deactivateUser / reactivateUser signatures now
   * require tenantId. Same `companyId` prop sourcing.
   */
  const handleToggleUserStatus = async (user) => {
    try {
      const isActive = !user.disabled;

      if (isActive) {
        await UserManagementService.deactivateUser(user.uid, companyId);
      } else {
        await UserManagementService.reactivateUser(user.uid, companyId);
      }
      
      // Refresh users
      await loadTenantUsers();
      
      console.log(`✅ User ${isActive ? 'deactivated' : 'reactivated'} successfully`);
    } catch (err) {
      console.error('❌ Failed to toggle user status:', err);
      setError(err.message);
    }
  };

  /**
   * Filter users based on search and filters
   */
  const filteredUsers = tenantUsers.filter(user => {
    // Search filter. Codex Round 3 P2: orphan rows have email: null and
    // displayName: null — guard both `.toLowerCase()` calls.
    if (searchTerm
        && !(user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
        && !(user.displayName || '').toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Role filter
    if (selectedRole !== 'all' && user.roleId !== selectedRole) {
      return false;
    }
    
    // Status filter
    if (selectedStatus === 'active' && user.disabled === true) return false;
    if (selectedStatus === 'inactive' && !user.disabled) return false;
    
    return true;
  });

  /**
   * Check if current user can manage another user
   */
  const canManageUser = (targetUser) => {
    return UserManagementService.canManageUser(userProfile, targetUser);
  };

  /**
   * Get available roles for tenant admin to assign
   */
  const getAssignableRoles = () => {
    return UserManagementService.getAssignableRoles(userProfile);
  };

  /**
   * Format date for display
   */
  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  /**
   * Format time ago
   */
  const formatTimeAgo = (date) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    
    return formatDate(date);
  };

  // Check if user has permission to manage users
  if (!UserManagementService.canInviteUsers(userProfile, companyId)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-red-500 text-4xl mb-4">🚫</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
            <p className="text-gray-600 mb-4">
              You don't have permission to manage users for this company.
            </p>
            <p className="text-sm text-gray-500">
              Only administrators can manage team members.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Team Members</h2>
          <p className="text-gray-600">Getting your team data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Team</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadTenantUsers}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/30">
      <div className="max-w-[1400px] mx-auto px-8 py-10 space-y-8">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-slate-800">Team Management</h1>
            <p className="text-slate-600">
              Manage users for {companyData?.name || 'your company'}
            </p>
          </div>
          
          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-slate-800 text-white px-4 py-2.5 rounded-lg hover:bg-slate-700 transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-sm"
          >
            <Plus size={16} />
            Invite Team Member
          </button>
        </div>

        {/* Stats Overview — CHA-97 / PR-C: "Recent Activity" dropped
            (lastSignInTime not surfaced by listUsers). */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatsCard
            title="Total Team Members"
            value={tenantStats.total || 0}
            icon={Users}
            color="blue"
            change={`${tenantStats.active || 0} active`}
          />
          <StatsCard
            title="Active Members"
            value={tenantStats.active || 0}
            icon={UserCheck}
            color="green"
            change={`${tenantStats.inactive || 0} inactive`}
          />
          <StatsCard
            title="Pending Invitations"
            value={tenantStats.pendingInvitations || 0}
            icon={Mail}
            color="amber"
            change="Awaiting response"
          />
        </div>

        {/* Role Breakdown */}
        {tenantStats.roleBreakdown && Object.keys(tenantStats.roleBreakdown).length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200/60 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Team Roles</h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(tenantStats.roleBreakdown).map(([role, count]) => (
                <div key={role} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                  <Shield size={14} className="text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">
                    {UserManagementService.formatRoleName(role)}
                  </span>
                  <span className="text-sm text-slate-500">({count})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h3 className="font-semibold text-amber-900 mb-4 flex items-center gap-2">
              <Mail size={16} />
              Pending Invitations ({pendingInvitations.length})
            </h3>
            <div className="space-y-3">
              {pendingInvitations.map((invitation) => (
                <div key={invitation.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <Mail size={14} className="text-amber-600" />
                    </div>
                    <div>
                      <div className="font-medium text-amber-900">{invitation.email}</div>
                      <div className="text-sm text-amber-700">
                        {UserManagementService.formatRoleName(invitation.role)} • 
                        Invited {formatTimeAgo(invitation.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        const baseUrl = window.location.origin;
                        const inviteLink = `${baseUrl}/invite/${invitation.id}`;
                        navigator.clipboard.writeText(inviteLink).then(() => {
                          alert('Invitation link copied to clipboard!');
                        }).catch(() => {
                          alert(`Invitation link: ${inviteLink}`);
                        });
                      }}
                      className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-lg transition-all duration-200"
                      title="Copy Invitation Link"
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      onClick={() => {
                        // TODO: Resend invitation functionality
                        alert('Resend invitation functionality coming soon!');
                      }}
                      className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-lg transition-all duration-200"
                      title="Resend Invitation"
                    >
                      <Send size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200/60 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  placeholder="Search team members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2.5 w-full border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 text-sm"
                />
              </div>
            </div>

            {/* Role Filter */}
            <div className="min-w-[150px]">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 text-sm"
              >
                <option value="all">All Roles</option>
                <option value="admin">Administrator</option>
                <option value="manager">Manager</option>
                <option value="user">User</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="min-w-[150px]">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Team Members Table */}
        <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-16">
              <div className="p-3 bg-slate-100 rounded-full w-fit mx-auto mb-4">
                <Users className="text-slate-400" size={24} />
              </div>
              <h3 className="text-lg font-medium text-slate-800 mb-2">No Team Members Found</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                {searchTerm || selectedRole !== 'all' || selectedStatus !== 'all'
                  ? 'Try adjusting your filters to see more team members'
                  : 'Start building your team by inviting your first member'
                }
              </p>
              {!(searchTerm || selectedRole !== 'all' || selectedStatus !== 'all') && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                >
                  Invite First Member
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200/60">
                    <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Team Member</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Role</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Status</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Last Login</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Joined</th>
                    <th className="text-right px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <TeamMemberRow
                      key={user.uid}
                      user={user}
                      currentUser={userProfile}
                      formatDate={formatDate}
                      formatTimeAgo={formatTimeAgo}
                      canManage={canManageUser(user)}
                      onEditUser={(user) => {
                        setSelectedUser(user);
                        setShowEditUserModal(true);
                      }}
                      onToggleStatus={() => handleToggleUserStatus(user)}
                      onViewUser={(user) => {
                        setSelectedUser(user);
                        setShowUserDetailsModal(true);
                      }}
                      isLast={index === filteredUsers.length - 1}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modals */}
        {showInviteModal && (
          <InviteTeamMemberModal
            companyData={companyData}
            assignableRoles={getAssignableRoles()}
            onClose={() => setShowInviteModal(false)}
            onSubmit={handleInviteUser}
          />
        )}

        {showEditUserModal && selectedUser && (
          <EditTeamMemberModal
            user={selectedUser}
            assignableRoles={getAssignableRoles()}
            onClose={() => {
              setShowEditUserModal(false);
              setSelectedUser(null);
            }}
            onSubmit={async (userData) => {
              await handleUpdateUserRole(userData.uid, userData.roleId);
            }}
          />
        )}

        {showUserDetailsModal && selectedUser && (
          <TeamMemberDetailsModal
            user={selectedUser}
            companyName={companyData?.name || 'Unknown Company'}
            onClose={() => {
              setShowUserDetailsModal(false);
              setSelectedUser(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Stats Card Component
 */
function StatsCard({ title, value, icon: Icon, color, change }) {
  const colorClasses = {
    blue: 'text-blue-600 bg-blue-100',
    green: 'text-green-600 bg-green-100',
    amber: 'text-amber-600 bg-amber-100',
    purple: 'text-purple-600 bg-purple-100',
    rose: 'text-rose-600 bg-rose-100'
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200/60 p-6">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon size={20} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-slate-600 mb-1">{title}</p>
          <p className="text-2xl font-semibold text-slate-900">{value}</p>
          {change && (
            <p className="text-xs text-slate-500 mt-1">{change}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Team Member Row Component
 */
function TeamMemberRow({ user, currentUser, formatDate, formatTimeAgo, canManage, onEditUser, onToggleStatus, onViewUser, isLast }) {
  const [showDropdown, setShowDropdown] = useState(false);

  const getStatusColor = (status) => {
    if (status === 'inactive') return 'bg-rose-100 text-rose-700';
    return 'bg-emerald-100 text-emerald-700';
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-blue-100 text-blue-700',
      manager: 'bg-green-100 text-green-700',
      user: 'bg-slate-100 text-slate-700',
      viewer: 'bg-amber-100 text-amber-700'
    };
    return colors[role] || 'bg-slate-100 text-slate-700';
  };

  const isCurrentUser = user.uid === currentUser.uid;

  return (
    <tr 
      className={`hover:bg-slate-50/50 transition-colors group ${
        isLast ? '' : 'border-b border-slate-100'
      }`}
    >
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-slate-600">
              {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              {/* Codex Round 3 P2: orphan rows have email: null +
                  displayName: null. Fall through to a generic
                  placeholder so the table renders the orphan for
                  cleanup. Same pattern at the two other render sites
                  below + the search filter above. */}
              <div className="font-medium text-slate-900">
                {user.displayName || (user.email && user.email.split('@')[0]) || 'Orphan user'}
              </div>
              {isCurrentUser && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                  You
                </span>
              )}
            </div>
            <div className="text-sm text-slate-500">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${getRoleColor(user.roleId)}`}>
          {UserManagementService.formatRoleName(user.roleId)}
        </span>
      </td>
      <td className="px-6 py-4">
        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(user.disabled ? 'inactive' : 'active')}`}>
          {user.disabled ? 'Inactive' : 'Active'}
        </span>
      </td>
      <td className="px-6 py-4 text-sm text-slate-600">
        {formatTimeAgo(user.lastLogin)}
      </td>
      <td className="px-6 py-4 text-sm text-slate-600">
        {formatDate(user.createdAt)}
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-1">
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200"
            >
              <MoreVertical size={14} />
            </button>
            
            {showDropdown && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowDropdown(false)}
                />
                <div className="absolute right-0 top-10 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20">
                  <button
                    onClick={() => {
                      onViewUser(user);
                      setShowDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <Eye size={14} />
                    View Details
                  </button>
                  {canManage && !isCurrentUser && (
                    <>
                      <button
                        onClick={() => {
                          onEditUser(user);
                          setShowDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                      >
                        <Edit3 size={14} />
                        Edit Role
                      </button>
                      <button
                        onClick={() => {
                          onToggleStatus();
                          setShowDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                      >
                        {user.disabled ? (
                          <>
                            <UserCheck size={14} />
                            Activate Member
                          </>
                        ) : (
                          <>
                            <UserX size={14} />
                            Deactivate Member
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

/**
 * Invite Team Member Modal Component
 */
function InviteTeamMemberModal({ companyData, assignableRoles, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'user',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      setError('Email address is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-xl border border-slate-200/60">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Invite Team Member</h2>
        <p className="text-sm text-slate-600 mb-8">
          Add a new member to {companyData?.name || 'your team'}
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200"
                placeholder="teammate@company.com"
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Full Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200"
                placeholder="John Doe"
                disabled={loading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Role *
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200"
              disabled={loading}
            >
              {assignableRoles.map(role => (
                <option key={role} value={role}>
                  {UserManagementService.formatRoleName(role)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Welcome Message (Optional)
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200 resize-none"
              rows={3}
              placeholder="Welcome to our team! We're excited to work with you."
              disabled={loading}
            />
          </div>

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
              <p className="text-rose-800 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all duration-200 text-sm font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-all duration-200 disabled:opacity-50 text-sm font-medium shadow-sm"
              disabled={loading}
            >
              {loading ? 'Sending Invitation...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Edit Team Member Modal Component
 */
function EditTeamMemberModal({ user, assignableRoles, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    uid: user.uid,
    roleId: user.roleId
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError(null);

    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-xl border border-slate-200/60">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Edit Team Member</h2>
        <p className="text-sm text-slate-600 mb-8">Update role and permissions</p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-slate-600">
                  {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="font-medium text-slate-900">
                  {user.displayName || (user.email && user.email.split('@')[0]) || 'Orphan user'}
                </div>
                <div className="text-sm text-slate-500">{user.email}</div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Role
            </label>
            <select
              value={formData.roleId}
              onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200"
              disabled={loading}
            >
              {assignableRoles.map(role => (
                <option key={role} value={role}>
                  {UserManagementService.formatRoleName(role)}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
              <p className="text-rose-800 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all duration-200 text-sm font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-all duration-200 disabled:opacity-50 text-sm font-medium shadow-sm"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Team Member Details Modal Component
 */
function TeamMemberDetailsModal({ user, companyName, onClose }) {
  return (
    <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-8 shadow-xl border border-slate-200/60">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-semibold text-slate-800">Team Member Details</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors text-2xl leading-none"
          >
            ×
          </button>
        </div>
        
        <div className="space-y-6">
          {/* User Header */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center">
              <span className="text-xl font-medium text-slate-600">
                {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                {user.displayName || (user.email && user.email.split('@')[0]) || 'Orphan user'}
              </h3>
              <p className="text-slate-600">{user.email}</p>
              <p className="text-sm text-slate-500">
                {UserManagementService.formatRoleName(user.roleId)} at {companyName}
              </p>
            </div>
          </div>

          {/* User Details Grid */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3">Account Information</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Status</label>
                  <p className="text-sm text-slate-900">
                    <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                      user.disabled ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {user.disabled ? 'Inactive' : 'Active'}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Role</label>
                  <p className="text-sm text-slate-900">{UserManagementService.formatRoleName(user.roleId)}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Company</label>
                  <p className="text-sm text-slate-900">{companyName}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3">Activity</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Joined</label>
                  <p className="text-sm text-slate-900">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Last Login</label>
                  <p className="text-sm text-slate-900">
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Last Updated</label>
                  <p className="text-sm text-slate-900">
                    {user.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions */}
          {user.permissions && (
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3">Permissions</h4>
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(user.permissions).map(([area, perms]) => (
                    <div key={area}>
                      <label className="text-xs text-slate-500 uppercase tracking-wide capitalize">
                        {area.replace('_', ' ')}
                      </label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {perms.map((perm, index) => (
                          <span key={index} className="px-2 py-1 text-xs bg-white rounded border text-slate-600">
                            {perm}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-6 mt-6 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}